import { useId } from "react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/** Select value standing in for "Same item" (no group field). */
const SAME_ITEM_VALUE = "same-item";

/** Prefix that keeps item field option values disjoint from SAME_ITEM_VALUE. */
const FIELD_VALUE_PREFIX = "field:";

interface PriceListBreakGroupFieldRowProps {
  value: string | null;
  itemFieldNames: string[];
  onChange: (next: string | null) => void;
  disabled: boolean;
}

/**
 * Picks what quote lines share to combine their quantities for price list breaks:
 * the same item, or the same value of an item custom field.
 * @param props - PriceListBreakGroupFieldRowProps
 * @returns The "Combine by" sub-row element
 */
export function PriceListBreakGroupFieldRow({
  value,
  itemFieldNames,
  onChange,
  disabled,
}: PriceListBreakGroupFieldRowProps) {
  const selectId = useId();
  const selectedValue = toSelectValue(value, itemFieldNames);

  return (
    <div className="ml-1 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-l pl-4">
      <div className="min-w-0 flex-1 basis-60">
        <Label htmlFor={selectId} className="text-sm font-normal">
          Combine by
        </Label>
        <p className="text-xs text-muted-foreground mt-0.5 max-w-md leading-snug">
          Lines whose items share the same value are added together. Items with
          a blank value combine with the same item only.
        </p>
      </div>
      <Select
        value={selectedValue}
        onValueChange={(next) => onChange(fromSelectValue(next))}
        disabled={disabled}
      >
        <SelectTrigger id={selectId} className="w-[220px] max-w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={SAME_ITEM_VALUE}>Same item</SelectItem>
          {itemFieldNames.length > 0 && (
            <SelectGroup>
              <SelectLabel>Item fields</SelectLabel>
              {itemFieldNames.map((fieldName) => (
                <SelectItem
                  key={fieldName}
                  value={`${FIELD_VALUE_PREFIX}${fieldName}`}
                >
                  {fieldName}
                </SelectItem>
              ))}
            </SelectGroup>
          )}
        </SelectContent>
      </Select>
    </div>
  );
}

/**
 * Encodes the saved group field as a select value.
 * @param value - The saved group field name, or null for "Same item"
 * @param itemFieldNames - The item custom field names that can be picked
 * @returns The prefixed field value, or SAME_ITEM_VALUE when no known field is set
 */
function toSelectValue(value: string | null, itemFieldNames: string[]) {
  if (!value || !itemFieldNames.includes(value)) {
    return SAME_ITEM_VALUE;
  }
  return `${FIELD_VALUE_PREFIX}${value}`;
}

/**
 * Decodes a select value back into the group field name.
 * @param selectValue - The value chosen in the select
 * @returns The field name for a prefixed field value, otherwise null for "Same item"
 */
function fromSelectValue(selectValue: string) {
  if (!selectValue.startsWith(FIELD_VALUE_PREFIX)) {
    return null;
  }
  return selectValue.slice(FIELD_VALUE_PREFIX.length);
}
